import { useAssistantRefresh } from '../hooks/useAssistantRefresh';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type DragEvent,
} from "react";
import {
  formatTaskKey,
  TASK_PRIORITY_LABEL,
  type TaskStatus,
  type TaskSummary,
} from "@todo/shared";
import type { ProgressSummary } from "@todo/shared";
import { ApiError } from "../api/client";
import { taskApi } from "../api/task.api";
import { overviewApi } from "../api/overview.api";
import { useAuth } from "../hooks/useAuth";
import { StarButton } from "../components/StarButton";
import { TaskDetailPanel } from "../components/TaskDetailPanel";
import { CollapsibleColumn } from "../components/CollapsibleColumn";
import { PRIORITY_BADGE, PRIORITY_DOT } from "../lib/task-ui";

/**
 * Module 2 - My Day, as a KANBAN BOARD (Azure Boards style).
 *
 * Four columns mirror the task lifecycle: Not started → In progress →
 * Blocked → Completed. Cards are today's work on MY plate (due today, plus
 * anything overdue - yesterday's unfinished work is still today's problem),
 * and dragging a card between columns IS the status change: the drop calls
 * PATCH /tasks/:id/status, so the board and the database can never disagree.
 *
 * Native HTML5 drag-and-drop - no library. The card carries its task id in
 * the drag payload; each column accepts the drop and asks the API to move it.
 */

type Column = Extract<TaskStatus, "PENDING" | "ONGOING" | "BLOCKED" | "DONE">;

/** Accent colours and the column chrome now live in CollapsibleColumn. */
const COLUMNS: Column[] = ["PENDING", "ONGOING", "BLOCKED", "DONE"];

function dayBounds(): { todayStart: string; tomorrowStart: string } {
  const now = new Date();
  return {
    todayStart: new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    ).toISOString(),
    tomorrowStart: new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 1,
    ).toISOString(),
  };
}

/** The end date shown on every card - just the date, no time: due dates
 *  are day-level ("due that day"), so a clock time only adds noise. */
function dueLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  });
}

/**
 * The strip takes the board's active chip as a PROP rather than holding
 * its own view state - it used to depend on [days] alone, so switching
 * chip refiltered the cards and left these numbers untouched. Two answers
 * to the same question on one screen is worse than either answer alone.
 */
function ProgressPanel({ view }: { view: 'all' | 'mine' | 'delegated' }) {
  const [data, setData] = useState<ProgressSummary | null>(null);
  const [days, setDays] = useState<7 | 30 | 90>(7);

  useEffect(() => {
    let cancelled = false;
    overviewApi
      .progress(days, view)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [days, view]);

  const rows: Array<{ label: string; value: number; dot: string }> = data
    ? [
        { label: "Not started", value: data.notStarted, dot: "bg-sky-400" },
        { label: "In progress", value: data.inProgress, dot: "bg-amber-400" },
        { label: "Blocked", value: data.blocked, dot: "bg-red-400" },
        { label: "Completed", value: data.completed, dot: "bg-emerald-400" },
      ]
    : [];

  const total = data
    ? data.notStarted + data.inProgress + data.blocked + data.completed
    : 0;
  const pct = (value: number) =>
    total > 0 ? Math.round((value / total) * 100) : 0;

  /*
   * One line, or none. Rendering an empty <p> still occupies its line box
   * and the mt-1 margin, so the header jumped by a few pixels whenever the
   * caption was blank - hence the whole element is conditional rather than
   * its text.
   */
  const caption =
    view === "mine"
      ? "Your work"
      : view === "delegated"
        ? "Handed to others"
        : data?.scope === "org"
          ? "Organisation-wide"
          : "";

  return (
    // A full-width HORIZONTAL strip above the board: title + period on the
    // left, the two period tiles, the segmented total bar, the per-status
    // legend, and Overdue - the board below keeps the whole width.
    <section className="mt-5 shrink-0 rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h2 className="text-base font-semibold text-slate-900">Project stats</h2>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value) as 7 | 30 | 90)}
              aria-label="Period"
              className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
          {/* RESERVED, not conditional. Hiding the element shortened the
              block and shoved the whole strip up a line; an empty one with
              no height did the same in reverse. min-h-4 matches text-xs's
              line box, so the row is the same height caption or not. */}
          <p className="mt-1 min-h-4 text-xs text-slate-400">{caption}</p>
        </div>

        {!data ? (
          <div className="h-16 flex-1 animate-pulse rounded-lg bg-slate-100" />
        ) : (
          <>
            {/* The period tiles: what HAPPENED in the chosen window. */}
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-5 py-3">
              <p className="text-2xl font-semibold tabular-nums text-slate-900">
                {data.createdInPeriod}
              </p>
              <p className="text-xs text-slate-500">Created</p>
              <p className="text-[11px] text-slate-400">last {data.days}d</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-5 py-3">
              <p className="text-2xl font-semibold tabular-nums text-emerald-600">
                {data.completedInPeriod}
              </p>
              <p className="text-xs text-slate-500">Completed</p>
              <p className="text-[11px] text-slate-400">last {data.days}d</p>
            </div>

            {/* Standing total, split by status as one segmented bar. */}
            <div className="min-w-52 max-w-xs flex-1">
              <p className="text-sm font-semibold text-slate-800">
                {total} {total === 1 ? "task" : "tasks"} total
              </p>
              <div className="mt-2 flex h-2 gap-0.5 overflow-hidden rounded-full bg-slate-100">
                {rows
                  .filter((row) => row.value > 0)
                  .map((row) => (
                    <span
                      key={row.label}
                      className={row.dot}
                      style={{ flex: row.value }}
                    />
                  ))}
                {total === 0 && <span className="flex-1 bg-slate-100" />}
              </div>
            </div>

            {/* Legend: count and share per status. */}
            <ul className="space-y-1">
              {rows.map((row) => (
                <li key={row.label} className="flex items-center gap-2 text-xs">
                  <span className={`h-2 w-2 shrink-0 rounded-full ${row.dot}`} />
                  <span className="w-20 text-slate-600">{row.label}</span>
                  <span className="w-6 text-right font-semibold tabular-nums text-slate-900">
                    {row.value}
                  </span>
                  <span className="w-10 text-right tabular-nums text-slate-400">
                    {pct(row.value)}%
                  </span>
                </li>
              ))}
            </ul>

            <div>
              <p className="text-sm text-slate-500">Overdue</p>
              <p
                className={`text-2xl font-semibold tabular-nums ${data.overdue > 0 ? "text-red-600" : "text-slate-900"}`}
              >
                {data.overdue}
              </p>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default function MyDayPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<TaskSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [viewing, setViewing] = useState<TaskSummary | null>(null);
  /** A drop onto Blocked needs a reason first; the move waits here. */
  const [blocking, setBlocking] = useState<TaskSummary | null>(null);
  const [blockReason, setBlockReason] = useState("");
  /** all = my work + what I delegated; the chips narrow it. */
  const [view, setView] = useState<"all" | "mine" | "delegated">("all");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // TWO scopes, merged: my plate (assigned to me + my unassigned) AND
      // what I delegated to others - the creator needs today's status of the
      // work they handed out without leaving the board. Both due before
      // tomorrow; completed included so the Done column shows finished work.
      const { tomorrowStart } = dayBounds();
      const [mine, delegated, mineStarted, delegatedStarted] = await Promise.all([
        taskApi.list({
          scope: "assigned-to-me",
          dueTo: tomorrowStart,
          pageSize: 100,
        }),
        taskApi.list({
          scope: "created-by-me",
          dueTo: tomorrowStart,
          pageSize: 100,
        }),
        /**
         * Work that has been STARTED belongs on today's board whatever its
         * due date - an In progress task due next week must not vanish from
         * My Day and be forgotten. Fetched without the due window and
         * merged; the dedupe below collapses any overlap.
         */
        taskApi.list({
          scope: "assigned-to-me",
          status: "ONGOING",
          pageSize: 100,
        }),
        taskApi.list({
          scope: "created-by-me",
          status: "ONGOING",
          pageSize: 100,
        }),
      ]);
      // Dedupe: a task I created AND am assigned to appears once.
      const byId = new Map<string, TaskSummary>();
      for (const t of [
        ...mine.data,
        ...delegated.data,
        ...mineStarted.data,
        ...delegatedStarted.data,
      ]) byId.set(t.id, t);

      // A sub-task is allowed to have NO due date of its own (it inherits
      // its urgency from the parent - see task.schema.ts), so the dueTo
      // filter above silently drops it even when its parent is sitting
      // right here on the board. Fetch each such parent's sub-tasks
      // separately, unfiltered by due date, so the day's breakdown of work
      // actually shows up alongside it.
      const parentIds = [...byId.values()]
        .filter((t) => t.parentTaskId === null && t.subtaskCount > 0)
        .map((t) => t.id);
      if (parentIds.length > 0) {
        const subtaskPages = await Promise.all(
          parentIds.map((parentId) =>
            taskApi.list({ parentId, pageSize: 100 }),
          ),
        );
        for (const page of subtaskPages) {
          for (const t of page.data) byId.set(t.id, t);
        }
      }

      setTasks(
        [...byId.values()].filter(
          (t) => t.status !== "DRAFT" && t.status !== "CANCELLED",
        ),
      );
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not load your day",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // The AI chat can change data server-side; refetch when it says so.
  useAssistantRefresh(load);

  const { todayStart } = dayBounds();

  /**
   * Delegated = I created it AND handed it to somebody else.
   * An UNASSIGNED task I created is NOT delegated - nobody else can even see
   * it, so it counts as my own work and stays under "My work".
   */
  const isDelegated = useCallback(
    (task: TaskSummary) =>
      task.createdById === user?.id &&
      task.assignees.length > 0 &&
      !task.assignees.some((a) => a.userId === user?.id),
    [user?.id],
  );

  /**
   * Same nesting rule as the Tasks page's board: a sub-task whose PARENT is
   * also visible here folds INTO the parent's card as a checklist row -
   * whatever column either of them stands in - instead of floating as a
   * card of its own. A sub-task whose parent is not on the board (filtered
   * out by the view chips, or not loaded) keeps its own card so it never
   * disappears.
   */
  const { byColumn, childrenOf } = useMemo(() => {
    const map: Record<Column, TaskSummary[]> = {
      PENDING: [],
      ONGOING: [],
      BLOCKED: [],
      DONE: [],
    };
    const visible = tasks.filter((task) => {
      if (!(task.status in map)) return false;
      if (view === "mine" && isDelegated(task)) return false;
      if (view === "delegated" && !isDelegated(task)) return false;
      return true;
    });
    const visibleIds = new Set(visible.map((t) => t.id));
    const children = new Map<string, TaskSummary[]>();
    for (const task of visible) {
      if (task.parentTaskId && visibleIds.has(task.parentTaskId)) {
        const siblings = children.get(task.parentTaskId) ?? [];
        siblings.push(task);
        children.set(task.parentTaskId, siblings);
      }
    }
    for (const task of visible) {
      if (task.parentTaskId && visibleIds.has(task.parentTaskId)) continue;
      map[task.status as Column].push(task);
    }
    // Completed is history: most recently finished first, so the capped
    // view below always shows the LATEST completions.
    map.DONE.sort((a, b) => b.lastActivityAt.localeCompare(a.lastActivityAt));
    return { byColumn: map, childrenOf: children };
  }, [tasks, view, isDelegated]);

  /** The Completed column shows this many cards; "View more" adds 10. */
  const [doneVisible, setDoneVisible] = useState(10);

  // Lookup by id, for pulling a sub-task's parent off the board it is
  // already sitting on (parent is always loaded alongside its sub-tasks -
  // see load() above).
  const taskById = useMemo(
    () => new Map(tasks.map((t) => [t.id, t])),
    [tasks],
  );

  /** A sub-task may have no due date of its own (see task.schema.ts) - it
   *  then falls back to showing its parent's due date, so the card is never
   *  blank. Returns null only when neither has one. */
  function dueAtFor(task: TaskSummary): string | null {
    if (task.dueAt) return task.dueAt;
    if (!task.parentTaskId) return null;
    return taskById.get(task.parentTaskId)?.dueAt ?? null;
  }

  function replace(updated: TaskSummary) {
    setTasks((current) =>
      current.map((t) => (t.id === updated.id ? updated : t)),
    );
  }

  async function move(task: TaskSummary, to: Column, blockedReason?: string) {
    if (task.status === to) return;
    if (to === "BLOCKED" && !blockedReason) {
      setBlocking(task); // collect the reason, then finish the move
      return;
    }

    setBusyId(task.id);
    try {
      replace(await taskApi.setStatus(task.id, to, blockedReason));
      if (to === "DONE") setFlash(`${formatTaskKey(task.taskKey)} completed.`);
    } catch (err) {
      setFlash(
        err instanceof ApiError ? err.message : "Could not move the task",
      );
    } finally {
      setBusyId(null);
      setBlocking(null);
      setBlockReason("");
    }
  }

  async function run(task: TaskSummary, action: () => Promise<TaskSummary>) {
    setBusyId(task.id);
    try {
      replace(await action());
    } catch (err) {
      setFlash(
        err instanceof ApiError ? err.message : "Could not update the task",
      );
    } finally {
      setBusyId(null);
    }
  }

  function onDrop(event: DragEvent, column: Column) {
    event.preventDefault();
    const id = event.dataTransfer.getData("text/plain");
    const task = tasks.find((t) => t.id === id);
    if (task) void move(task, column);
  }

  const heading = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  const openCount = tasks.filter((t) => t.status !== "DONE").length;

  return (
    // h-full (one unscrolled screen) only at xl+, where board and stats sit
    // side by side. On a small laptop the stats stack BELOW the board, so
    // the page must be allowed to grow and scroll instead.
    <div className="flex min-h-full flex-col px-8 py-8 xl:h-full">
      <header className="shrink-0">
        <h1 className="text-xl font-semibold text-slate-900">My Day</h1>
        <p className="mt-1 text-sm text-slate-500">
          {heading}
          {!loading &&
            ` · ${openCount} open ${openCount === 1 ? "task" : "tasks"} · drag a card to change its status`}
        </p>

        <div className="mt-3 flex gap-1.5">
          {(
            [
              ["all", "All"],
              ["mine", "My work"],
              ["delegated", "Assigned by me"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setView(value)}
              className={[
                "rounded-full px-3 py-1 text-xs font-medium ring-1 transition",
                view === value
                  ? "bg-slate-900 text-white ring-slate-900"
                  : "bg-white text-slate-600 ring-slate-300 hover:bg-slate-50",
              ].join(" ")}
            >
              {label}
            </button>
          ))}
        </div>
      </header>

      {flash && (
        <div
          role="status"
          className="mt-4 flex shrink-0 items-start justify-between gap-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-800 ring-1 ring-emerald-200"
        >
          <span>{flash}</span>
          <button
            type="button"
            onClick={() => setFlash(null)}
            aria-label="Dismiss"
            className="text-emerald-600 hover:text-emerald-800"
          >
            &times;
          </button>
        </div>
      )}

      <ProgressPanel view={view} />

      {error ? (
        <div className="mt-5 rounded-xl border border-slate-200 bg-white px-5 py-12 text-center">
          <p className="text-sm text-red-600">{error}</p>
          <button
            type="button"
            onClick={() => void load()}
            className="mt-3 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50"
          >
            Try again
          </button>
        </div>
      ) : (
        // The board gets the FULL width - Project stats sits above it now.
        <div className="mt-5 flex min-h-[420px] flex-1 justify-between gap-4 overflow-x-auto pb-4 xl:min-h-0">
            {COLUMNS.map((key) => {
              const full = byColumn[key];
              // Completed capped to the latest few; live columns show all.
              const list = key === "DONE" ? full.slice(0, doneVisible) : full;
              const hiddenDone = full.length - list.length;
              return (
                <CollapsibleColumn
                  key={key}
                  column={key}
                  countLabel={full.length}
                  onDrop={(e, col) => onDrop(e, col as Column)}
                >
                  <>
                    {loading ? (
                      [0, 1].map((i) => (
                        <div
                          key={i}
                          className="h-20 animate-pulse rounded-lg border border-slate-200 bg-white"
                        />
                      ))
                    ) : list.length === 0 ? (
                      <p className="px-1 py-6 text-center text-xs text-slate-400">
                        {key === "PENDING"
                          ? "Nothing waiting"
                          : "Drop a card here"}
                      </p>
                    ) : (
                      list.map((task) => {
                        const effectiveDueAt = dueAtFor(task);
                        const dueIsInherited = !task.dueAt && !!effectiveDueAt;
                        const overdue =
                          !!effectiveDueAt &&
                          effectiveDueAt < todayStart &&
                          task.status !== "DONE";
                        const busy = busyId === task.id;
                        const isSubtask = !!task.parentTaskId;

                        return (
                          <article
                            key={task.id}
                            draggable={!busy}
                            onDragStart={(e) =>
                              e.dataTransfer.setData("text/plain", task.id)
                            }
                            onClick={(e) => {
                              if ((e.target as HTMLElement).closest("button"))
                                return;
                              setViewing(task);
                            }}
                            className={[
                              "cursor-grab rounded-lg border bg-white shadow-sm transition hover:border-slate-300 active:cursor-grabbing",
                              isSubtask ? "p-2" : "p-3",
                              overdue ? "border-red-200" : "border-slate-200",
                              busy ? "opacity-60" : "",
                            ].join(" ")}
                          >
                            <div className="flex items-center gap-1.5">
                              {isSubtask && (
                                <span
                                  className="text-slate-300"
                                  aria-hidden="true"
                                >
                                  &#8627;
                                </span>
                              )}
                              <span
                                className={`h-2 w-2 shrink-0 rounded-full ${PRIORITY_DOT[task.priority]}`}
                                title={`${task.priority}`}
                              />
                             <div>
                               <span
                                className={`font-mono text-slate-400 ${isSubtask ? "text-[10px]" : "text-[11px]"}`}
                              >
                                {formatTaskKey(task.taskKey)}
                              </span>
                              <span
                                className={`mt-1 ml-2 inline-flex w-fit items-center rounded px-1.5 py-0.5 text-[10px] font-semibold ${PRIORITY_BADGE[task.priority]}`}
                              >
                                {TASK_PRIORITY_LABEL[task.priority]}
                              </span>
                             </div>
                              {isSubtask && (
                                <span className="rounded bg-slate-100 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                                  Sub
                                </span>
                              )}
                              {effectiveDueAt && (
                                <span
                                  className={`ml-auto shrink-0 text-[11px] font-medium ${overdue ? "text-red-600" : "text-slate-400"}`}
                                  title={
                                    dueIsInherited
                                      ? "Due date inherited from the parent task"
                                      : undefined
                                  }
                                >
                                  {dueLabel(effectiveDueAt)}
                                </span>
                              )}
                            </div>

                            {/* Two lines for a task; a sub-task's smaller card
                              allows only one - it is already saying "look at
                              the parent for the full picture". */}
                            <p
                              className={[
                                "mt-1.5 font-medium leading-snug text-slate-900",
                                isSubtask
                                  ? "line-clamp-1 text-xs"
                                  : "line-clamp-2 text-sm",
                              ].join(" ")}
                            >
                              {task.title}
                            </p>

                            {/* Only a TASK carries this - a sub-task cannot
                              have sub-tasks of its own. */}
                            {!isSubtask && task.subtaskCount > 0 && (
                              <div className="mt-1.5 flex items-center gap-1.5">
                                <div className="h-1 flex-1 overflow-hidden rounded-full bg-slate-100">
                                  <div
                                    className="h-full rounded-full bg-emerald-400"
                                    style={{
                                      width: `${(task.subtaskDoneCount / task.subtaskCount) * 100}%`,
                                    }}
                                  />
                                </div>
                                <span className="text-[10px] tabular-nums text-slate-400">
                                  {Math.round(
                                    (task.subtaskDoneCount /
                                      task.subtaskCount) *
                                      100,
                                  )}
                                  %
                                </span>
                              </div>
                            )}

                            {/* Sub-tasks on this board fold into their
                                parent's card - the Azure Boards checklist
                                look, same as the Tasks page. Rows never
                                drag; the PARENT is the card. */}
                            {!isSubtask && (childrenOf.get(task.id)?.length ?? 0) > 0 && (
                              <ul className="mt-1.5 space-y-1 rounded-md bg-slate-50 p-1.5 ring-1 ring-slate-100">
                                {(childrenOf.get(task.id) ?? []).map((sub) => {
                                  const subDone = sub.status === "DONE";
                                  return (
                                    <li key={sub.id} className="flex items-center gap-1.5">
                                      <button
                                        type="button"
                                        disabled={busyId === sub.id}
                                        title={subDone ? "Reopen sub-task" : "Mark sub-task complete"}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          void move(sub, subDone ? "ONGOING" : "DONE");
                                        }}
                                        className={[
                                          "grid h-[14px] w-[14px] shrink-0 place-items-center rounded-[4px] text-[8px] font-bold transition disabled:opacity-40",
                                          subDone
                                            ? "bg-emerald-600 text-white hover:bg-emerald-700"
                                            : "border-[1.5px] border-slate-300 bg-white text-transparent hover:border-emerald-500 hover:text-emerald-500",
                                        ].join(" ")}
                                      >
                                        &#10003;
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setViewing(sub); }}
                                        title={`${formatTaskKey(sub.taskKey)} · ${sub.status}`}
                                        className="min-w-0 flex-1 truncate text-left text-[11px] leading-tight hover:underline"
                                      >
                                        <span className={subDone ? "text-slate-400 line-through" : "text-slate-700"}>
                                          {sub.title}
                                        </span>
                                      </button>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}

                            {isDelegated(task) ? (
                              <p
                                className={`mt-0.5 flex flex-wrap items-center gap-1 ${isSubtask ? "text-[10px]" : "text-[11px]"}`}
                              >
                                <span className="rounded bg-violet-50 px-1 py-px font-semibold uppercase tracking-wide text-violet-700 ring-1 ring-violet-200">
                                  Assigned by you
                                </span>
                                {task.assignees.slice(0, 2).map((a) => (
                                  <span key={a.userId} className="text-slate-500">
                                    {a.displayName.split(" ")[0]}
                                  </span>
                                ))}
                                {task.assignees.length > 2 && (
                                  <span className="text-slate-400">
                                    +{task.assignees.length - 2}
                                  </span>
                                )}
                              </p>
                            ) : (
                              <p
                                className={`mt-0.5 text-slate-400 ${isSubtask ? "text-[10px]" : "text-[11px]"}`}
                              >
                                {task.createdById === user?.id
                                  ? task.assignees.length === 0
                                    ? "Created by you · unassigned"
                                    : "Created by you"
                                  : task.createdByName}
                              </p>
                            )}

                            <div className="mt-2 flex items-center gap-1.5">
                              <span className="ml-auto">
                                <StarButton
                                  active={task.isImportant}
                                  busy={busy}
                                  onToggle={() =>
                                    void run(task, () =>
                                      taskApi.setImportant(
                                        task.id,
                                        !task.isImportant,
                                      ),
                                    )
                                  }
                                />
                              </span>
                            </div>
                          </article>
                        );
                      })
                    )}
                    {!loading && hiddenDone > 0 && (
                      <button
                        type="button"
                        onClick={() => setDoneVisible((v) => v + 10)}
                        className="mt-2 w-full rounded-lg border border-dashed border-slate-300 bg-white px-2 py-2 text-center text-xs font-semibold text-brand-700 transition hover:bg-brand-50"
                      >
                        View {Math.min(10, hiddenDone)} more
                        <span className="mt-0.5 block text-[10.5px] font-medium text-slate-400">
                          {hiddenDone} not shown
                        </span>
                      </button>
                    )}
                  </>
                </CollapsibleColumn>
              );
            })}
        </div>
      )}

      {blocking && (
        <div className="fixed inset-0 z-40 grid place-items-center bg-slate-900/40 p-4">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void move(blocking, "BLOCKED", blockReason.trim());
            }}
            className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-5 shadow-xl"
          >
            <h2 className="text-base font-semibold text-slate-900">
              Block {formatTaskKey(blocking.taskKey)}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Say what is blocking it - the reason is recorded on the task.
            </p>
            <input
              autoFocus
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              maxLength={500}
              placeholder="Waiting on client approval"
              className="mt-4 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setBlocking(null);
                  setBlockReason("");
                }}
                className="rounded-lg px-3.5 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!blockReason.trim() || busyId === blocking.id}
                className="rounded-lg bg-red-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-red-700 disabled:opacity-60"
              >
                Mark blocked
              </button>
            </div>
          </form>
        </div>
      )}
      {viewing && (
        <TaskDetailPanel
          task={viewing}
          onClose={() => setViewing(null)}
          onChanged={(t) => {
            replace(t);
            setViewing((v) => (v && v.id === t.id ? t : v));
          }}
        />
      )}
    </div>
  );
}
